// =============================================================================
// Customer Add/Edit — faithful merge of IMS Customer_Add.vb + POS frmNewCustomer.
// Fields: name parts, company, full contact set (home/home2/work/work2/mobile/fax),
// two emails, address block, directions, store credit/balance, notes + confidential
// notes, active flag, birthday. Duplicate check on save (last name+address+city+state+zip).
// =============================================================================
import { useState } from 'react'
import { X } from 'lucide-react'
import { createCustomer, updateCustomer, checkDuplicateCustomer } from '../services/data'
import { Card, Button } from './shared/ui'
import type { Customer } from '../types'
import { toast } from 'react-toastify'

type Mode = 'new' | 'edit'

interface Props {
  mode: Mode
  customer?: Customer | null
  onClose: () => void
  onSaved: (c: Customer, opts: { keepOpen: boolean }) => void
}

const empty = {
  first_name: '', last_name: '', company_name: '', name: '',
  phone_home: '', phone_home2: '', phone_work: '', phone_work2: '', mobile: '', phone: '', fax: '',
  email: '', email2: '',
  address: '', city: '', state: '', zip: '', directions: '',
  store_credit: '', balance: '',
  comments: '', confidential_notes: '', notes: '',
  is_active: true,
}

function Field({ label, value, onChange, className = '', type = 'text' }: {
  label: string; value: string; onChange: (v: string) => void; className?: string; type?: string
}) {
  return (
    <div className={className}>
      <label className="text-xs font-medium text-gray-500">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
      />
    </div>
  )
}

export default function CustomerForm({ mode, customer, onClose, onSaved }: Props) {
  const init = customer
    ? { ...empty, ...Object.fromEntries(Object.keys(empty).map(k => [k, (customer as Record<string, unknown>)[k] ?? empty[k as keyof typeof empty]])) }
    : { ...empty }
  const [f, setF] = useState<typeof empty>(init as typeof empty)
  const [saving, setSaving] = useState(false)
  const set = (k: keyof typeof empty, v: string | boolean) => setF(prev => ({ ...prev, [k]: v }))

  const build = (): Partial<Customer> => ({
    first_name: f.first_name || null,
    last_name: f.last_name || null,
    company_name: f.company_name || null,
    // Keep a combined display name in sync (POS uses single name field).
    name: f.name || [f.first_name, f.last_name].filter(Boolean).join(' ') || f.company_name || null,
    phone_home: f.phone_home || null,
    phone_home2: f.phone_home2 || null,
    phone_work: f.phone_work || null,
    phone_work2: f.phone_work2 || null,
    mobile: f.mobile || null,
    phone: f.phone || f.phone_home || null,
    fax: f.fax || null,
    email: f.email || null,
    email2: f.email2 || null,
    address: f.address || null,
    city: f.city || null,
    state: f.state || null,
    zip: f.zip || null,
    directions: f.directions || null,
    store_credit: f.store_credit ? Number(f.store_credit) : 0,
    balance: f.balance ? Number(f.balance) : 0,
    comments: f.comments || null,
    confidential_notes: f.confidential_notes || null,
    notes: f.notes || null,
    is_active: f.is_active,
  })

  const doSave = async (keepOpen: boolean) => {
    if (!f.last_name && !f.company_name && !f.name) {
      toast.error('Enter at least a last name or company name')
      return
    }
    setSaving(true)
    try {
      const dup = await checkDuplicateCustomer(
        { last_name: f.last_name, address: f.address, city: f.city, state: f.state, zip: f.zip },
        mode === 'edit' ? customer?.id : undefined,
      )
      if (dup && !window.confirm(
        'There appears to be a duplicate customer with this last name, address, city, state, and zip code.\n\nAre you certain you wish to proceed?'
      )) {
        setSaving(false)
        return
      }

      const payload = build()
      const saved = mode === 'edit' && customer
        ? await updateCustomer(customer.id, payload)
        : await createCustomer(payload)
      toast.success('Record saved!')
      if (keepOpen) setF({ ...empty })
      onSaved(saved, { keepOpen })
    } catch (e) {
      console.error(e)
      toast.error('Failed to save customer')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-lg">{mode === 'edit' ? 'Edit Customer' : 'New Customer'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <div className="p-5 overflow-y-auto space-y-5">
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Name</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="First Name" value={f.first_name} onChange={v => set('first_name', v)} />
              <Field label="Last Name" value={f.last_name} onChange={v => set('last_name', v)} />
              <Field label="Company Name" value={f.company_name} onChange={v => set('company_name', v)} />
            </div>
          </section>

          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Contact</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="Home Phone" value={f.phone_home} onChange={v => set('phone_home', v)} />
              <Field label="Home Phone 2" value={f.phone_home2} onChange={v => set('phone_home2', v)} />
              <Field label="Work Phone" value={f.phone_work} onChange={v => set('phone_work', v)} />
              <Field label="Work Phone 2" value={f.phone_work2} onChange={v => set('phone_work2', v)} />
              <Field label="Mobile" value={f.mobile} onChange={v => set('mobile', v)} />
              <Field label="Fax" value={f.fax} onChange={v => set('fax', v)} />
              <Field label="Email" value={f.email} onChange={v => set('email', v)} className="md:col-span-1" />
              <Field label="Email 2" value={f.email2} onChange={v => set('email2', v)} className="md:col-span-1" />
            </div>
          </section>

          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Address</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="Address" value={f.address} onChange={v => set('address', v)} className="col-span-2 md:col-span-2" />
              <Field label="City" value={f.city} onChange={v => set('city', v)} />
              <Field label="State" value={f.state} onChange={v => set('state', v)} />
              <Field label="Zip" value={f.zip} onChange={v => set('zip', v)} />
            </div>
            <div className="mt-3">
              <label className="text-xs font-medium text-gray-500">Directions</label>
              <textarea value={f.directions} onChange={e => set('directions', e.target.value)} rows={2}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" />
            </div>
          </section>

          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Account</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="Store Credit" value={f.store_credit} onChange={v => set('store_credit', v)} type="number" />
              <Field label="Balance" value={f.balance} onChange={v => set('balance', v)} type="number" />
              <label className="flex items-center gap-2 mt-6 text-sm text-gray-700">
                <input type="checkbox" checked={f.is_active} onChange={e => set('is_active', e.target.checked)} /> Active
              </label>
            </div>
          </section>

          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Notes</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-500">Comments</label>
                <textarea value={f.comments} onChange={e => set('comments', e.target.value)} rows={3}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Confidential Notes</label>
                <textarea value={f.confidential_notes} onChange={e => set('confidential_notes', e.target.value)} rows={3}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" />
              </div>
            </div>
          </section>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose} disabled={saving}>Close</Button>
          {mode === 'new' && <Button variant="secondary" onClick={() => doSave(true)} disabled={saving}>Save &amp; New</Button>}
          <Button onClick={() => doSave(false)} disabled={saving}>{saving ? 'Saving…' : 'Save & Close'}</Button>
        </div>
      </Card>
    </div>
  )
}
